import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { onArt, typography, vault, vaultEffects, vaultLayout, vaultMedia, vaultRadii } from './design-tokens';
import { calendarStyles } from './calendar-styles';

const focusRing = {
  '&:focus-visible': { outline: `2px solid ${vault.accent}`, outlineOffset: 2 },
};

const hideScrollbar = {
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
} as const;

// Fixed heights for everything below the slab. The virtualized list needs a
// uniform row height, so AllCards derives it from these same numbers.
export const CARD_METRICS = {
  desktop: {
    slabPadding: 5,
    infoGap: 12,
    nameHeight: 20,
    metaHeight: 16,
    pricesGap: 10,
    priceHeight: 26,
    priceHeightStacked: 36,
    stripGap: 8,
    cellHeight: 30,
    cellHeightTouch: 40,
    columnGap: 18,
    rowGap: 24,
  },
  mobile: {
    slabPadding: 4,
    infoGap: 10,
    nameHeight: 18,
    metaHeight: 16,
    pricesGap: 8,
    priceHeight: 32,
    priceHeightStacked: 36,
    stripGap: 8,
    cellHeight: 40,
    cellHeightTouch: 40,
    columnGap: 12,
    rowGap: 20,
  },
};

// Narrowest card column before the grid adds another (desktop auto-fill).
export const CARD_COL_MIN_WIDTH = 220;

// Sticky filter rail height (also the sticky offset of the virtual list).
export const RAIL_HEIGHT = { desktop: 58, mobile: 64 };

export const allCardsStyles: Record<string, SystemStyleObject<Theme>> = {
  page: {
    minHeight: '100vh',
    backgroundColor: vault.bg,
    color: vault.fg,
  },
  statusMessage: {
    padding: `48px ${vaultLayout.gutter} 64px`,
    fontSize: 16,
    color: vault.muted,
    [vaultMedia.mobile]: { padding: `32px ${vaultLayout.gutterMobile} 40px` },
  },

  // ─── Header ────────────────────────────────────────────────────────────────
  header: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '20px 32px',
    padding: `48px ${vaultLayout.gutter} 28px`,
    [vaultMedia.mobile]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: '8px',
      padding: `20px ${vaultLayout.gutterMobile} 20px`,
    },
  },
  backRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '14px',
    [vaultMedia.mobile]: { marginBottom: '6px', justifyContent: 'space-between' },
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: 13,
    color: vault.muted,
    textDecoration: 'none',
    borderRadius: '4px',
    transition: `color ${vaultEffects.transition}`,
    '&:hover': { color: vault.fg },
    ...focusRing,
    [vaultMedia.mobile]: { minHeight: vaultLayout.tapTarget, fontSize: 14 },
  },
  title: {
    margin: 0,
    fontSize: 56,
    lineHeight: 1,
    fontWeight: 600,
    letterSpacing: '-0.035em',
    color: vault.fg,
    [vaultMedia.mobile]: { fontSize: 38 },
  },
  titleCount: {
    color: vault.faint,
    fontVariantNumeric: 'tabular-nums',
  },
  blurb: {
    margin: '14px 0 0',
    maxWidth: 620,
    fontSize: 14,
    lineHeight: 1.5,
    color: vault.muted,
    '& svg': { verticalAlign: '-2px', color: vault.faint },
    [vaultMedia.mobile]: { margin: '8px 0 4px', fontSize: 14 },
  },
  headerAside: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
    [vaultMedia.mobile]: { gap: 0 },
  },
  summary: {
    margin: 0,
    fontSize: 13,
    color: vault.muted,
    '& strong': { color: vault.fg, fontWeight: 500 },
    [vaultMedia.mobile]: { fontSize: 15 },
  },
  sortButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    minHeight: 40,
    padding: '9px 14px',
    border: `1px solid ${vault.lineStrong}`,
    borderRadius: '9px',
    backgroundColor: vault.surface,
    color: vault.fg,
    fontFamily: 'inherit',
    fontSize: 13,
    cursor: 'pointer',
    transition: `border-color ${vaultEffects.transition}`,
    '&:hover': { borderColor: vault.muted },
    '&:disabled': { opacity: 0.5, cursor: 'default' },
    ...focusRing,
    [vaultMedia.mobile]: { display: 'none' },
  },

  // ─── Filter rail ───────────────────────────────────────────────────────────
  rail: {
    position: 'sticky',
    top: 0,
    zIndex: 20,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    boxSizing: 'border-box',
    height: RAIL_HEIGHT.desktop,
    padding: `0 ${vaultLayout.gutter}`,
    backgroundColor: vault.bg,
    borderTop: `1px solid ${vault.line}`,
    borderBottom: `1px solid ${vault.line}`,
    [vaultMedia.mobile]: {
      height: RAIL_HEIGHT.mobile,
      padding: `0 ${vaultLayout.gutterMobile}`,
    },
  },
  railChips: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
    overflowX: 'auto',
    ...hideScrollbar,
    [vaultMedia.mobile]: {
      flex: 1,
      // Let chips scroll edge to edge on phones.
      margin: `0 -${vaultLayout.gutterMobile}`,
      padding: `0 ${vaultLayout.gutterMobile}`,
    },
  },
  showLabel: {
    marginRight: '8px',
    [vaultMedia.mobile]: { display: 'none' },
  },
  signInHint: {
    marginLeft: '8px',
    fontSize: 13,
    color: vault.muted,
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    borderBottom: `1px solid ${vault.lineStrong}`,
    '&:hover': { color: vault.fg },
    ...focusRing,
  },
  mobileOnly: {
    display: 'none',
    [vaultMedia.mobile]: { display: 'inline-flex' },
  },
  railSpacer: {
    flex: 1,
    [vaultMedia.mobile]: { display: 'none' },
  },
  reprintsToggle: {
    margin: 0,
    gap: '10px',
    flex: 'none',
    '& .MuiFormControlLabel-label': { fontSize: 13, color: vault.muted },
    [vaultMedia.mobile]: { display: 'none' },
  },
  switch: calendarStyles.switch,

  // ─── Sort menu ─────────────────────────────────────────────────────────────
  menuPaper: {
    marginTop: '6px',
    minWidth: 220,
    borderRadius: vaultRadii.card,
    backgroundColor: vault.surface,
    backgroundImage: 'none',
    border: `1px solid ${vault.lineStrong}`,
    boxShadow: `0 20px 60px ${vault.shadow}`,
    color: vault.fg,
    '& .MuiList-root': { padding: '6px' },
    '& .MuiDivider-root': { borderColor: vault.line, margin: '6px 0' },
  },
  menuItem: {
    gap: '10px',
    minHeight: 40,
    padding: '9px 12px',
    borderRadius: vaultRadii.input,
    fontSize: 14,
    color: vault.fg,
    justifyContent: 'space-between',
    '&:hover, &.Mui-focusVisible': { backgroundColor: vault.chipStrong },
    '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: vault.chip, color: vault.accentText },
    '& svg': { color: vault.accentText, flex: 'none' },
  },

  // ─── Grid ──────────────────────────────────────────────────────────────────
  gridSection: {
    padding: `28px ${vaultLayout.gutter} 64px`,
    [vaultMedia.mobile]: { padding: `20px ${vaultLayout.gutterMobile} 40px` },
  },
  emptyMessage: {
    margin: '0 auto',
    maxWidth: 640,
    padding: '48px 0',
    textAlign: 'center',
    fontSize: 15,
    lineHeight: 1.5,
    color: vault.muted,
  },
  expandButton: {
    display: 'inline-flex',
    marginTop: '16px',
    padding: '10px 16px',
    border: 'none',
    borderRadius: vaultRadii.input,
    backgroundColor: vault.accent,
    color: vault.onAccent,
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': { filter: 'brightness(1.08)' },
    ...focusRing,
  },

  // ─── Card item ─────────────────────────────────────────────────────────────
  card: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  artWrap: {
    position: 'relative',
  },
  artLink: {
    display: 'block',
    borderRadius: vaultRadii.slab,
    '&:focus-visible': { outline: `2px solid ${vault.accent}`, outlineOffset: 3 },
  },
  signedBadge: {
    fontFamily: typography.fontFamily.mono,
    fontSize: 10,
    letterSpacing: '.1em',
    padding: '4px 8px',
  },
  flipButton: {
    position: 'absolute',
    bottom: 14,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 40,
    height: 40,
    backgroundColor: onArt.badgeBg,
    backdropFilter: onArt.badgeBlur,
    color: onArt.text,
    border: '1px solid rgba(255,255,255,.12)',
    '&:hover': { backgroundColor: 'rgba(15,14,13,.92)' },
    ...focusRing,
  },
  name: {
    margin: 0,
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: '-0.01em',
    color: vault.fg,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    [vaultMedia.mobile]: { fontSize: 15 },
  },
  meta: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  prices: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'minmax(0, 1fr)',
    gap: '6px',
  },
  // Collection toggles: a segmented strip with hairline dividers. The 1px grid
  // gap shows the line-colored container through as dividers.
  strip: {
    display: 'grid',
    gap: '1px',
    overflow: 'hidden',
    borderRadius: '9px',
    border: `1px solid ${vault.line}`,
    backgroundColor: vault.line,
  },
  stripCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    minWidth: 0,
    padding: 0,
    border: 'none',
    borderRadius: 0,
    backgroundColor: vault.bg,
    fontFamily: 'inherit',
    transition: `background-color ${vaultEffects.transition}, color ${vaultEffects.transition}`,
    '&:hover': { backgroundColor: vault.chip },
    '&:focus-visible': { outline: `2px solid ${vault.accent}`, outlineOffset: -2 },
  },
  stripLabel: {
    fontSize: 9,
    lineHeight: 1,
    color: 'inherit',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  },

  scrollToTopFab: calendarStyles.scrollToTopFab,
};
